/**
 * L1J Web Client - Inventory Component
 * 인벤토리 UI 컴포넌트
 */

import Phaser from 'phaser';
import { resolveNpcName, resolveItemLabel, stripCountSuffix } from '../../utils/nameResolver';
import {
  itemIconKind,
  itemIconTextureKey,
  loadItemIcon,
  preloadItemIcons,
} from '../../data/itemIcons';
import { DragGhost } from './DragGhost';

export interface InventoryItem {
  id: number;
  gfxId: number;
  count: number;
  name: string;
  identified: boolean;
  enchantLevel: number;
  slot: number;
  /**
   * 장착 중인지. 서버가 표시명 끝에 붙이는 접미사로 판별한다
   * (utils/equipment.ts). 값이 없으면 미장착으로 본다.
   */
  equipped?: boolean;
}

/**
 * 장착 중 표시 색. 슬롯 테두리·'E' 뱃지·하단 정보 문구가 같은 색을 써야
 * "이 초록색 = 장착"이라는 규칙이 한눈에 읽힌다.
 */
const EQUIPPED_TEXT_COLOR = '#7ce87c';
const EQUIPPED_BORDER_COLOR = 0x4ad24a;

/** 대상 선택 모드 안내 문구 색. TargetPrompt 배너와 같은 강조색을 쓴다. */
const TARGET_MODE_TEXT_COLOR = '#ffd700';

/**
 * 대상 지정 직후 더블클릭을 무시하는 시간(ms).
 *
 * 대상 슬롯을 두 번 빠르게 누르면 첫 클릭으로 대상 지정이 끝나고 모드가 해제된 뒤
 * 두 번째 클릭이 그대로 "아이템 사용"(더블클릭)으로 이어진다. InventorySlot의
 * 더블클릭 판정(300ms)보다 넉넉하게 잡아 그 사고를 막는다.
 */
const TARGET_MODE_CLICK_GUARD_MS = 400;

/** 드래그로 인정하는 최소 이동 거리(px). 이보다 짧으면 그냥 클릭이다. */
const DRAG_THRESHOLD = 5;

/**
 * 그리드 치수. 인스턴스 필드가 아니라 모듈 상수인 이유는 setPosition() 오버라이드가
 * 이 값을 쓰기 때문이다 — Phaser Container 생성자가 필드 초기화보다 **먼저**
 * setPosition()을 부르므로 인스턴스 필드를 읽으면 undefined다.
 */
const SLOT_SIZE = 36;
const SLOT_GAP = 4;
const GRID_COLS = 4;
/**
 * 한 화면에 보이는 행 수. 인벤토리 용량이 아니다 — 넘치는 만큼은 스크롤로 본다.
 * 8행(패널 400px)이면 우상단 미니맵을 파고들어 7행으로 줄였다 (RESERVED_TOP_Y 참조).
 */
const GRID_ROWS = 7;

const GRID_WIDTH = GRID_COLS * SLOT_SIZE + (GRID_COLS - 1) * SLOT_GAP;
const GRID_HEIGHT = GRID_ROWS * SLOT_SIZE + (GRID_ROWS - 1) * SLOT_GAP;

/** 패널 폭 = 그리드 + 좌우 여백 20씩. */
const PANEL_WIDTH = GRID_WIDTH + 40;
/**
 * 패널 높이. 그리드 높이에서 역산한다 — 상수로 적어 두면 행 수를 바꿨을 때
 * 그리드가 하단 정보칸을 파고든다.
 *
 * 그리드 상단 테두리 = -H/2 + 32, 정보칸 상단 = H/2 - 50 이므로
 * H = GRID_HEIGHT + 82 에서 둘이 정확히 맞닿고, 여기에 2px 틈을 준 값이다.
 */
const PANEL_HEIGHT = GRID_HEIGHT + 84;

/**
 * 이 창이 침범하면 안 되는 화면 위쪽 영역의 아래끝(px, 논리 해상도 1024×768 기준).
 *
 * 우상단 미니맵은 GameScene이 (width-156, 105)에 놓고, 프레임이 원점에서 아래로
 * 146px 뻗는다 (MiniMap.ts: mapHeight 140 + framePadY 6) → 아래끝 251.
 * 인벤토리는 화면 오른쪽 끝에 붙으므로 가로가 겹치는 것은 피할 수 없다.
 * 그래서 세로로 미니맵 아래에서 시작하도록 setPosition()에서 잘라 낸다.
 */
const RESERVED_TOP_Y = 259;

/** 화면 아래끝과의 최소 여백. 위로 밀린 창이 화면 밖으로 나가지 않게 한다. */
const SCREEN_BOTTOM_MARGIN = 8;

/** 슬롯 테두리/배경 (평상시 · 마우스 오버 · 선택). */
const SLOT_BORDER_COLOR = 0x3a3a5a;
const SLOT_HOVER_COLOR = 0x6a6a8a;
const SLOT_SELECTED_COLOR = 0xffd700;
const SLOT_BG_COLOR = 0x2a2a3a;
/** 장착 중인 슬롯의 배경. 테두리만으로는 스크롤 중에 눈에 덜 띈다. */
const SLOT_BG_EQUIPPED_COLOR = 0x24382a;

export interface InventoryCallbacks {
  onUseItem?: (itemId: number) => void;
  onEquipItem?: (itemId: number) => void;
  /**
   * 대상 선택 모드(setTargetSelectMode(true))에서 슬롯을 클릭했을 때.
   * 인자는 "사용할 아이템"이 아니라 **대상으로 지정된 아이템**의 objId다.
   */
  onSelectTarget?: (itemId: number) => void;
  /** 창의 X 버튼으로 닫혔을 때. 씬이 대상 선택 모드를 접을 수 있게 알린다. */
  onClose?: () => void;

  /**
   * 아이템을 끌기 시작했다 (임계값을 넘은 시점).
   *
   * 이 창은 어디에 놓을 수 있는지 모른다 — 드롭 대상(퀵슬롯 바 등)을 아는 쪽은
   * 씬이므로, 판정과 처리는 전부 씬에 맡기고 여기서는 진행 상황만 알린다.
   */
  onItemDragStart?: (item: InventoryItem) => void;
  /** 끄는 중. 드롭 대상 강조를 갱신하라는 신호다. */
  onItemDragMove?: (item: InventoryItem, pointer: Phaser.Input.Pointer) => void;
  /** 손을 뗐다. 창 밖에 떨어뜨린 경우도 포함해 매번 온다. */
  onItemDragEnd?: (item: InventoryItem, pointer: Phaser.Input.Pointer) => void;
}

export class Inventory extends Phaser.GameObjects.Container {
  private items: InventoryItem[] = [];
  private slots: InventorySlot[] = [];
  private callbacks: InventoryCallbacks;

  private slotSize: number = SLOT_SIZE;
  private slotGap: number = SLOT_GAP;
  private cols: number = GRID_COLS;
  private rows: number = GRID_ROWS;
  private maxSlots: number = GRID_COLS * GRID_ROWS;

  /** 슬롯 그리드의 실제 폭. cols를 바꾸면 패널 폭도 따라와야 한다. */
  private gridWidth: number = GRID_WIDTH;

  private panelWidth: number = PANEL_WIDTH;
  private panelHeight: number = PANEL_HEIGHT;

  /** 첫 슬롯 중심의 Y. 그리드 세로 배치의 기준점. */
  private gridCenterTop: number = -PANEL_HEIGHT / 2 + 50;
  /** 슬롯 그리드의 실제 높이 (테두리 기준). */
  private gridHeight: number = GRID_HEIGHT;
  /** 그리드 상단 테두리의 Y. */
  private gridTop: number = -PANEL_HEIGHT / 2 + 50 - SLOT_SIZE / 2;

  /**
   * 스크롤 위치(행 단위). 슬롯은 maxSlots개로 고정하고 표시 구간만 옮기는
   * 가상 스크롤이다 — 아이템 수만큼 슬롯을 만들지 않으므로 개수가 늘어도
   * 오브젝트 수가 그대로다.
   */
  private scrollRow: number = 0;

  private scrollTrack?: Phaser.GameObjects.Rectangle;
  private scrollThumb?: Phaser.GameObjects.Rectangle;
  /** 트랙을 누른 채 끄는 중인지. 트랙 밖으로 나가도 이어져야 해서 씬 단위로 추적한다. */
  private isScrubbing: boolean = false;

  /**
   * 선택 상태는 슬롯이 아니라 아이템에 묶는다.
   * 스크롤하면 슬롯↔아이템 대응이 바뀌므로 슬롯에 표시를 남기면 엉뚱한 아이템이 선택돼 보인다.
   */
  private selectedItemId: number | null = null;

  /**
   * 마우스가 올라가 있는 아이템. 선택과 같은 이유로 슬롯이 아니라 아이템에 묶는다.
   * 하단 정보줄은 "hover > 선택 > 안내문" 순으로 우선한다.
   */
  private hoveredItemId: number | null = null;

  /**
   * 대상 선택 모드. 켜져 있는 동안 슬롯 클릭은 "아이템 선택/사용"이 아니라
   * "대상 지정"이다 (확인/강화 스크롤 등 use_type 7/14/26/27).
   * 모드를 켜고 끄는 주체는 GameScene의 대상 선택 상태 머신 하나뿐이다.
   */
  private targetSelectMode: boolean = false;

  /** 대상 선택 모드가 끝난 시각. 직후의 더블클릭을 무시하는 데 쓴다. */
  private targetModeEndedAt: number = 0;

  /** 씬 input에 직접 건 리스너. destroy 때 해제해야 씬 재시작 후 유령 리스너가 남지 않는다. */
  private sceneInput: Phaser.Input.InputPlugin;
  private onWheel!: (pointer: Phaser.Input.Pointer, objs: unknown, dx: number, dy: number) => void;
  private onPointerMove!: (pointer: Phaser.Input.Pointer) => void;
  private onPointerUp!: (pointer: Phaser.Input.Pointer) => void;

  /**
   * 슬롯을 눌렀지만 아직 드래그로 인정되지 않은 상태.
   * 임계값(DRAG_THRESHOLD)을 넘어야 draggingItem으로 승격한다.
   */
  private dragCandidate: { item: InventoryItem; x: number; y: number } | null = null;
  /** 실제로 끌고 있는 아이템. null이면 드래그 중이 아니다. */
  private draggingItem: InventoryItem | null = null;
  /** 커서를 따라다니는 미리보기. 이 컨테이너의 자식이라 UI 카메라 배정을 물려받는다. */
  private dragGhost!: DragGhost;

  constructor(scene: Phaser.Scene, x: number, y: number, callbacks?: InventoryCallbacks) {
    super(scene, x, y);
    this.callbacks = callbacks || {};
    this.sceneInput = scene.input;
    scene.add.existing(this);

    // 인벤토리 창도 화면에 고정
    this.setScrollFactor(0);

    this.createUI();
    this.registerScrollInput();
    this.setDepth(200);

    // 아이콘 23종은 통틀어도 몇 KB라 한 번에 받아둔다. 슬롯이 그려질 때
    // 텍스처가 이미 있으면 setItem()이 동기 경로로 빠져 깜빡임이 없다.
    // 실패는 내부에서 삼키므로(폴백은 '*' 텍스트) 호출부에서 기다리지 않는다.
    void preloadItemIcons(scene);
  }

  /**
   * 창을 우상단 미니맵 아래로 밀어 낸다.
   *
   * 배치를 정하는 쪽은 GameScene이지만 그쪽은 "화면 오른쪽 끝 · 세로 중앙"만 알고
   * 미니맵 높이는 모른다. 그 결과 창 위쪽 70px 남짓이 미니맵과 겹쳤다.
   * 겹침을 아는 쪽이 이 창이므로 여기서 세로 위치만 걸러 낸다 — 호출부는 그대로 둔다.
   *
   * 여기서 읽는 값이 전부 모듈 상수인 것은 의도적이다. Phaser Container 생성자가
   * 필드 초기화 전에 setPosition()을 부르므로 인스턴스 필드는 아직 undefined다.
   */
  public setPosition(x?: number, y?: number, z?: number, w?: number): this {
    if (typeof y === 'number') {
      const minCenterY = RESERVED_TOP_Y + PANEL_HEIGHT / 2;
      const screenHeight = this.scene?.scale?.height ?? 768;
      const maxCenterY = screenHeight - PANEL_HEIGHT / 2 - SCREEN_BOTTOM_MARGIN;
      // 화면이 세로로 너무 짧으면 아래로 잘리더라도 미니맵을 피하는 쪽을 택한다.
      y = Math.min(Math.max(y, minCenterY), Math.max(minCenterY, maxCenterY));
    }
    return super.setPosition(x, y, z, w);
  }

  private createUI(): void {
    // 패널 배경
    const panelBg = this.scene.add.rectangle(
      0,
      0,
      this.panelWidth,
      this.panelHeight,
      0x1a1a2e,
      0.95
    );
    panelBg.setStrokeStyle(2, 0x4a4a6a);
    this.add(panelBg);

    // 타이틀 바
    const titleBar = this.scene.add.rectangle(
      0,
      -this.panelHeight / 2 + 15,
      this.panelWidth,
      30,
      0x2a2a4e
    );
    titleBar.setStrokeStyle(1, 0x3a3a5a);
    this.add(titleBar);

    const titleText = this.scene.add.text(0, -this.panelHeight / 2 + 15, 'Inventory', {
      fontSize: '14px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold'
    });
    titleText.setOrigin(0.5);
    this.add(titleText);

    // 개수/스크롤 위치 표시. 그리드 위아래에는 2px밖에 남지 않으므로 타이틀 바
    // 왼쪽 빈자리('Inventory'는 가운데, X는 오른쪽)를 쓴다.
    const countText = this.scene.add.text(
      -this.panelWidth / 2 + 10,
      -this.panelHeight / 2 + 15,
      '',
      {
        fontSize: '10px',
        fontFamily: 'Arial, sans-serif',
        color: '#9a9ac0'
      }
    );
    countText.setOrigin(0, 0.5);
    countText.name = 'countText';
    this.add(countText);

    // 닫기 버튼
    const closeBtn = this.scene.add.text(
      this.panelWidth / 2 - 20,
      -this.panelHeight / 2 + 15,
      'X',
      {
        fontSize: '14px',
        fontFamily: 'Arial, sans-serif',
        color: '#ff6666'
      }
    );
    closeBtn.setOrigin(0.5);
    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다
    // (카메라 스크롤 중 클릭 히트테스트가 어긋나는 문제 방지).
    closeBtn.setInteractive({ useHandCursor: true });
    closeBtn.setScrollFactor(0);
    closeBtn.on('pointerdown', () => this.close());
    this.add(closeBtn);

    // 인벤토리 슬롯들 생성
    this.createSlots();

    // 슬롯 그리드 오른쪽 스크롤바
    this.createScrollBar();

    // 하단 정보 영역
    this.createInfoArea();

    // 드래그 미리보기는 마지막에 붙여 슬롯들 위에 그려지게 한다.
    // (Container는 클리핑하지 않으므로 패널 밖으로 끌어도 계속 보인다.)
    this.dragGhost = new DragGhost(this.scene, this.slotSize);
    this.add(this.dragGhost);

    // 아직 아이템을 받기 전이라도 '0 / 0'으로 채워 빈칸을 남기지 않는다.
    this.updateCountText();
  }

  /**
   * 스크롤바. 아이템이 한 화면(maxSlots)에 다 들어가면 숨는다.
   *
   * 썸은 순수 시각 요소이고 입력은 트랙이 받는다 — 썸은 내용량에 따라 높이가 바뀌는데
   * Phaser에서 setSize()는 이미 등록된 hitArea를 갱신하지 않아 클릭 판정이 어긋난다.
   */
  private createScrollBar(): void {
    // 그리드 오른쪽 끝과 패널 오른쪽 끝 사이 여백의 중앙
    const trackX = (this.gridWidth / 2 + this.panelWidth / 2) / 2;
    const trackY = this.gridTop + this.gridHeight / 2;
    const trackWidth = 6;

    this.scrollTrack = this.scene.add.rectangle(
      trackX,
      trackY,
      trackWidth,
      this.gridHeight,
      0x0a0a15
    );
    this.scrollTrack.setStrokeStyle(1, 0x2a2a4a);
    this.scrollTrack.setScrollFactor(0);
    // 6px 트랙은 그대로 두면 집기 어려우므로 판정 영역만 좌우로 넓힌다.
    // hitArea 좌표는 오브젝트 원점이 아니라 좌상단 기준(0,0)~(width,height)이다.
    this.scrollTrack.setInteractive(
      new Phaser.Geom.Rectangle(-(24 - trackWidth) / 2, 0, 24, this.gridHeight),
      Phaser.Geom.Rectangle.Contains
    );
    this.scrollTrack.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isScrubbing = true;
      this.scrubToPointer(pointer);
    });
    this.add(this.scrollTrack);

    this.scrollThumb = this.scene.add.rectangle(trackX, trackY, trackWidth, 40, 0x4a4a6a);
    this.scrollThumb.setScrollFactor(0);
    this.add(this.scrollThumb);

    this.updateScrollBar();
  }

  private createSlots(): void {
    // 슬롯 원점은 중앙이므로 그리드 폭 기준으로 첫 슬롯 중심을 잡는다.
    // 패널 폭에서 역산하면 cols를 바꿨을 때 그리드가 한쪽으로 쏠린다.
    const startX = -this.gridWidth / 2 + this.slotSize / 2;
    const startY = -this.panelHeight / 2 + 50;

    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const slotX = startX + col * (this.slotSize + this.slotGap);
        const slotY = startY + row * (this.slotSize + this.slotGap);
        const slotIndex = row * this.cols + col;

        const slot = new InventorySlot(this.scene, slotX, slotY, this.slotSize, slotIndex, {
          onClick: this.handleSlotClick.bind(this),
          onDoubleClick: this.handleSlotDoubleClick.bind(this),
          onHover: this.handleSlotHover.bind(this),
          onHoverEnd: this.handleSlotHoverEnd.bind(this),
          onPress: this.handleSlotPress.bind(this),
        });

        this.slots.push(slot);
        this.add(slot);
      }
    }
  }

  private createInfoArea(): void {
    const infoY = this.panelHeight / 2 - 30;

    // 아이템 설명 영역 (나중에 구현)
    const infoBg = this.scene.add.rectangle(
      0,
      infoY,
      this.panelWidth - 40,
      40,
      0x0a0a15
    );
    infoBg.setStrokeStyle(1, 0x2a2a4a);
    this.add(infoBg);

    // 4열로 좁아진 패널에서 긴 아이템 이름이 창 밖으로 삐져나가지 않도록 줄바꿈시킨다.
    const infoText = this.scene.add.text(0, infoY, '아이템에 마우스를 올리세요', {
      fontSize: '12px',
      fontFamily: 'Arial, sans-serif',
      color: '#888888',
      align: 'center',
      wordWrap: { width: this.panelWidth - 50 }
    });
    infoText.setOrigin(0.5);
    infoText.name = 'infoText';
    this.add(infoText);
  }

  /**
   * 휠 스크롤과 스크롤바 드래그를 씬 input에 등록한다.
   *
   * 슬롯이 패널 배경 위를 덮고 있어 오브젝트 단위 wheel 이벤트로는 슬롯 위에서 굴린 휠을
   * 놓친다. 그래서 씬 전역에서 받고 포인터가 패널 안인지 직접 판정한다.
   */
  private registerScrollInput(): void {
    this.onWheel = (pointer, _objs, _dx, dy) => {
      if (!this.visible || !this.isPointerOverPanel(pointer)) return;
      this.scrollByRows(dy > 0 ? 1 : -1);
    };

    // 트랙 밖으로 포인터가 나가도 드래그가 이어져야 하므로 씬 단위로 받는다.
    // 아이템 드래그도 같은 이유로 여기서 받는다 — 놓는 곳(퀵슬롯 바)은 이 창 밖이다.
    this.onPointerMove = (pointer) => {
      if (this.isScrubbing) {
        this.scrubToPointer(pointer);
        return;
      }
      this.updateItemDrag(pointer);
    };
    this.onPointerUp = (pointer) => {
      this.isScrubbing = false;
      this.endItemDrag(pointer);
    };

    this.sceneInput.on('wheel', this.onWheel);
    this.sceneInput.on('pointermove', this.onPointerMove);
    this.sceneInput.on('pointerup', this.onPointerUp);

    // 씬이 재시작돼도 유령 리스너가 남지 않도록 파괴 시 해제한다.
    this.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.sceneInput.off('wheel', this.onWheel);
      this.sceneInput.off('pointermove', this.onPointerMove);
      this.sceneInput.off('pointerup', this.onPointerUp);
    });
  }

  /** 포인터가 패널 사각형 안에 있는지. 패널은 scrollFactor 0이라 화면 좌표로 바로 비교한다. */
  private isPointerOverPanel(pointer: Phaser.Input.Pointer): boolean {
    const dx = Math.abs(pointer.x - this.x);
    const dy = Math.abs(pointer.y - this.y);
    return dx <= this.panelWidth / 2 && dy <= this.panelHeight / 2;
  }

  /** 스크롤 가능한 최대 행. 0이면 스크롤할 내용이 없다. */
  private getMaxScrollRow(): number {
    return Math.max(0, Math.ceil(this.items.length / this.cols) - this.rows);
  }

  private scrollByRows(delta: number): void {
    this.setScrollRow(this.scrollRow + delta);
  }

  private setScrollRow(row: number): void {
    const clamped = Phaser.Math.Clamp(row, 0, this.getMaxScrollRow());
    if (clamped === this.scrollRow) return;
    this.scrollRow = clamped;
    // 스크롤하면 커서 아래 슬롯의 내용이 바뀌는데 pointerover는 다시 오지 않는다.
    // hover 표시를 그대로 두면 커서와 관계없는 아이템 정보가 남는다.
    this.hoveredItemId = null;
    this.renderSlots();
    this.refreshInfoText();
  }

  /** 트랙에서의 포인터 Y를 스크롤 행으로 환산한다. */
  private scrubToPointer(pointer: Phaser.Input.Pointer): void {
    const maxScroll = this.getMaxScrollRow();
    if (maxScroll === 0) return;

    // 컨테이너 로컬 좌표로 변환 후 트랙 내 비율(0~1)로 환산
    const localY = pointer.y - this.y;
    const ratio = Phaser.Math.Clamp((localY - this.gridTop) / this.gridHeight, 0, 1);
    this.setScrollRow(Math.round(ratio * maxScroll));
  }

  /** 스크롤 위치·내용량에 맞춰 썸의 크기와 위치를 다시 계산한다. */
  private updateScrollBar(): void {
    if (!this.scrollTrack || !this.scrollThumb) return;

    const maxScroll = this.getMaxScrollRow();
    const needed = maxScroll > 0;
    this.scrollTrack.setVisible(needed);
    this.scrollThumb.setVisible(needed);
    if (!needed) return;

    const totalRows = Math.ceil(this.items.length / this.cols);
    const thumbHeight = Math.max(20, (this.gridHeight * this.rows) / totalRows);
    const travel = this.gridHeight - thumbHeight;

    this.scrollThumb.setSize(this.scrollThumb.width, thumbHeight);
    this.scrollThumb.y = this.gridTop + thumbHeight / 2 + (this.scrollRow / maxScroll) * travel;
  }

  /**
   * 현재 스크롤 위치에 해당하는 구간만 슬롯에 그린다.
   * 슬롯 i에는 items[scrollRow * cols + i]가 들어간다.
   */
  private renderSlots(): void {
    const offset = this.scrollRow * this.cols;

    this.slots.forEach((slot, i) => {
      const item = this.items[offset + i];
      if (item) {
        slot.setItem(item);
        slot.setSelected(item.id === this.selectedItemId);
      } else {
        slot.clear();
        // clear()는 선택 테두리를 지우지 않으므로 직접 해제한다.
        slot.setSelected(false);
      }
    });

    this.updateScrollBar();
    this.updateCountText();
  }

  /**
   * 타이틀 바의 "보이는 범위 / 총 개수" 표시를 갱신한다.
   *
   * 그리드는 아이템 전체가 아니라 한 구간만 보여주므로, 이 표시가 없으면
   * 화면을 꽉 채운 그리드가 "이게 전부"인지 "아래에 더 있는지"를 구분할 수 없다.
   */
  private updateCountText(): void {
    const countText = this.getByName('countText') as Phaser.GameObjects.Text;
    if (!countText) return;

    const total = this.items.length;
    if (total === 0) {
      countText.setText('0 / 0');
      return;
    }

    const first = this.scrollRow * this.cols + 1;
    const last = Math.min(total, first - 1 + this.maxSlots);
    countText.setText(`${first}-${last} / ${total}`);
  }

  private handleSlotClick(slot: InventorySlot, item: InventoryItem | null): void {
    if (!item) return;

    // 대상 선택 모드에서는 클릭이 "선택"이 아니라 "대상 지정"이다.
    // 선택 테두리/정보 문구는 건드리지 않는다 — 모드가 끝나면 원래 선택이 그대로 남아야 한다.
    if (this.targetSelectMode) {
      this.callbacks.onSelectTarget?.(item.id);
      return;
    }

    // 선택 효과 (선택은 슬롯이 아니라 아이템에 묶는다 — 스크롤하면 대응이 바뀐다)
    this.selectedItemId = item.id;
    this.slots.forEach((s) => s.setSelected(false));
    slot.setSelected(true);

    // 정보 업데이트
    this.refreshInfoText();
  }

  // ==================== 아이템 드래그 ====================

  /**
   * 슬롯을 눌렀다. 드래그 후보로만 기록하고 실제 시작은 임계값을 넘을 때 판정한다
   * (누르자마자 끌기 시작하면 평범한 클릭 선택이 매번 드래그가 된다).
   *
   * 대상 선택 모드에서는 클릭이 "대상 지정"이라 드래그를 걸지 않는다.
   */
  private handleSlotPress(item: InventoryItem | null, pointer: Phaser.Input.Pointer): void {
    if (!item || this.targetSelectMode) return;
    this.dragCandidate = { item, x: pointer.x, y: pointer.y };
  }

  private updateItemDrag(pointer: Phaser.Input.Pointer): void {
    if (this.draggingItem) {
      this.dragGhost.followTo(pointer.x - this.x, pointer.y - this.y);
      this.callbacks.onItemDragMove?.(this.draggingItem, pointer);
      return;
    }

    if (!this.dragCandidate) return;
    const moved = Phaser.Math.Distance.Between(
      this.dragCandidate.x,
      this.dragCandidate.y,
      pointer.x,
      pointer.y
    );
    if (moved < DRAG_THRESHOLD) return;

    // 임계값을 넘었다 — 여기서부터 드래그다.
    this.draggingItem = this.dragCandidate.item;
    this.dragCandidate = null;
    this.dragGhost.show(
      {
        gfxId: this.draggingItem.gfxId,
        label: this.draggingItem.name,
        count: this.draggingItem.count,
      },
      pointer.x - this.x,
      pointer.y - this.y
    );
    this.callbacks.onItemDragStart?.(this.draggingItem);
    this.callbacks.onItemDragMove?.(this.draggingItem, pointer);
  }

  private endItemDrag(pointer: Phaser.Input.Pointer): void {
    this.dragCandidate = null;
    if (!this.draggingItem) return;

    const item = this.draggingItem;
    this.draggingItem = null;
    this.dragGhost.hide();
    this.callbacks.onItemDragEnd?.(item, pointer);
  }

  /** 지금 아이템을 끌고 있는지. 씬이 드롭 처리 여부를 판단할 때 쓴다. */
  public isDraggingItem(): boolean {
    return this.draggingItem !== null;
  }

  /**
   * 슬롯에 마우스가 올라왔다. 하단 정보줄을 그 아이템으로 바꾼다.
   *
   * 정보줄 안내가 '아이템에 마우스를 올리세요'인데 실제로는 클릭해야만 값이 채워졌다 —
   * 안내대로 hover에서도 채운다. 마우스를 빼면 선택 중인 아이템으로 되돌아간다.
   */
  private handleSlotHover(item: InventoryItem | null): void {
    if (!item) return;
    this.hoveredItemId = item.id;
    this.refreshInfoText();
  }

  private handleSlotHoverEnd(item: InventoryItem | null): void {
    // 지금 정보줄에 떠 있는 아이템의 슬롯에서 벗어날 때만 지운다.
    // 빈 슬롯은 애초에 hover를 세우지 않으므로 벗어나도 할 일이 없고,
    // 옆 슬롯으로 넘어가 pointerover가 먼저 온 경우에도 새 아이템이 지워지지 않는다.
    if (!item || this.hoveredItemId !== item.id) return;
    this.hoveredItemId = null;
    this.refreshInfoText();
  }

  private handleSlotDoubleClick(_slot: InventorySlot, item: InventoryItem | null): void {
    if (!item) return;

    // 대상 선택 모드에서는 더블클릭도 대상 지정으로만 취급한다.
    if (this.targetSelectMode) {
      this.callbacks.onSelectTarget?.(item.id);
      return;
    }

    // 대상 지정으로 모드가 막 끝났다면, 이어진 두 번째 클릭은 사용이 아니다.
    if (Date.now() - this.targetModeEndedAt < TARGET_MODE_CLICK_GUARD_MS) return;

    // 더블 클릭 시 아이템 사용/장착
    this.callbacks.onUseItem?.(item.id);
  }

  /**
   * 대상 선택 모드를 켜고 끈다.
   *
   * 모드가 켜져 있는 동안 슬롯 클릭은 `onSelectTarget`으로만 나간다 —
   * 이 창은 "무엇을 사용하는 중인지"를 모른다. 그 상태는 GameScene이 갖는다.
   */
  public setTargetSelectMode(enabled: boolean): void {
    if (this.targetSelectMode === enabled) return;
    this.targetSelectMode = enabled;
    if (!enabled) {
      this.targetModeEndedAt = Date.now();
    }
    // 대상 선택 중에는 아이템 정보 대신 무엇을 골라야 하는지를 보여준다.
    this.refreshInfoText();
  }

  /** 지금 대상 선택 모드인지. */
  public isTargetSelectMode(): boolean {
    return this.targetSelectMode;
  }

  /**
   * 하단 정보줄을 지금 상태로 다시 그린다. 정보줄을 건드리는 곳은 여기 하나뿐이다.
   *
   * 우선순위: 대상 선택 안내 > 마우스가 올라간 아이템 > 선택한 아이템 > 기본 안내.
   * 아이템 값은 매번 `this.items`에서 다시 찾는다 — 문구를 채울 때 복사해 두면
   * 장착/해제나 개수 변화가 서버에서 돌아와도 옛 값이 남는다.
   */
  private refreshInfoText(): void {
    const infoText = this.getByName('infoText') as Phaser.GameObjects.Text;
    if (!infoText) return;

    if (this.targetSelectMode) {
      infoText.setText('대상 아이템을 선택하세요 (ESC 취소)');
      infoText.setStyle({ color: TARGET_MODE_TEXT_COLOR });
      return;
    }

    const shownId = this.hoveredItemId ?? this.selectedItemId;
    const item = shownId === null ? undefined : this.items.find((it) => it.id === shownId);
    if (!item) {
      infoText.setText('아이템에 마우스를 올리세요');
      infoText.setStyle({ color: '#888888' });
      return;
    }

    this.updateItemInfo(item);
  }

  /**
   * 창을 닫고 씬에 알린다.
   *
   * X 버튼이 setVisible(false)만 호출하면 씬은 창이 닫힌 줄 모르고, 대상 선택
   * 모드가 보이지 않는 창에 남아 다음 클릭이 엉뚱하게 동작한다.
   */
  private close(): void {
    this.setVisible(false);
    this.callbacks.onClose?.();
  }

  private updateItemInfo(item: InventoryItem): void {
    const infoText = this.getByName('infoText') as Phaser.GameObjects.Text;
    if (!infoText) return;

    const enchantText = item.enchantLevel > 0 ? `+${item.enchantLevel}` : '';
    const identifiedText = item.identified ? '' : ' [미감정]';

    // 겹치는 아이템은 서버가 이름 꼬리에 개수를 박아 보낸다
    // ("음식 (15)", "$3301 (9)"). 진실은 count 필드뿐이므로 꼬리를 떼고
    // x{count}만 쓴다 (stripCountSuffix 주석 참조).
    const rawName = stripCountSuffix(item.name);
    let displayName = resolveNpcName(rawName);
    if (displayName.includes('($')) {
      // 이름 안에 ($숫자) 상태 라벨이 끼어 있으면 resolveItemLabel로 치환 시도
      displayName = displayName.replace(/\(\$\d+\)/g, (m) => resolveItemLabel(m));
    }

    const equippedText = item.equipped ? ' [장착 중]' : '';

    infoText.setText(`${displayName}${enchantText}${identifiedText}${equippedText} x${item.count}`);
    infoText.setStyle({ color: item.equipped ? EQUIPPED_TEXT_COLOR : '#ffffff' });
  }

  /**
   * 아이템 목록 업데이트
   *
   * 인벤토리가 바뀌면 이 함수가 다시 불려 화면이 갱신된다. 구독은 창이 아니라
   * 씬(GameScene)이 한다 — `gameEvents.on('inventory:update')` → `applyInventory()`
   * → 여기. 전체 수신(inventory)·단건 추가/삭제(add_item/delete_item)·개수 변경
   * (item_amount)이 모두 GameState를 거쳐 그 한 이벤트로 모이므로, 창이 따로
   * 구독하면 같은 목록을 두 번 그리게 된다.
   */
  public updateItems(items: InventoryItem[]): void {
    // slot 번호 순으로 정렬해 표시 순서를 고정한다. 게이트웨이가 slot을 주지 않으면
    // GameScene이 배열 인덱스를 넣으므로 이 경우 원래 순서가 그대로 유지된다.
    this.items = [...items].sort((a, b) => a.slot - b.slot);

    // 아이템이 줄어 현재 스크롤 위치가 범위를 벗어났으면 되돌린다.
    this.scrollRow = Phaser.Math.Clamp(this.scrollRow, 0, this.getMaxScrollRow());

    this.renderSlots();
    this.refreshInfoText();
  }

  /**
   * 특정 슬롯 번호의 아이템을 교체(없으면 추가)한다.
   *
   * 인자는 화면상의 칸 위치가 아니라 아이템의 slot 번호다 —
   * 스크롤 때문에 화면 칸과 아이템은 1:1로 고정되지 않는다.
   */
  public setItem(slot: number, item: InventoryItem): void {
    const index = this.items.findIndex((it) => it.slot === slot);
    if (index >= 0) {
      this.items[index] = item;
    } else {
      this.items.push(item);
      this.items.sort((a, b) => a.slot - b.slot);
    }
    this.renderSlots();
    this.refreshInfoText();
  }

  /**
   * 특정 슬롯 번호의 아이템을 제거한다. (인자 의미는 setItem과 동일)
   */
  public clearSlot(slot: number): void {
    const index = this.items.findIndex((it) => it.slot === slot);
    if (index < 0) return;

    if (this.items[index].id === this.selectedItemId) {
      this.selectedItemId = null;
    }
    if (this.items[index].id === this.hoveredItemId) {
      this.hoveredItemId = null;
    }
    this.items.splice(index, 1);
    this.scrollRow = Phaser.Math.Clamp(this.scrollRow, 0, this.getMaxScrollRow());
    this.renderSlots();
    this.refreshInfoText();
  }

  /**
   * 한 화면에 보이는 슬롯 수. 인벤토리 전체 용량이 아니다 —
   * 이를 넘는 아이템은 스크롤로 접근한다.
   */
  public getMaxSlots(): number {
    return this.maxSlots;
  }

  /**
   * 패널 폭. 화면 가장자리에 정렬할 때 필요하다 —
   * 폭은 cols에서 계산되므로 호출부가 값을 따로 알고 있으면 어긋난다.
   */
  public getPanelWidth(): number {
    return this.panelWidth;
  }

  /** 패널 높이. 클릭이 창 위에서 일어났는지 판정할 때 쓴다. */
  public getPanelHeight(): number {
    return this.panelHeight;
  }
}

/**
 * 인벤토리 슬롯이 부모(Inventory)에게 올려 보내는 입력들.
 *
 * `onPress`는 클릭/더블클릭과 별개로 **누른 순간마다** 온다 — 드래그를 시작할지
 * 판단하는 쪽이 부모이기 때문이다 (슬롯은 커서가 창 밖으로 나간 뒤를 알지 못한다).
 */
interface InventorySlotHandlers {
  onClick: (slot: InventorySlot, item: InventoryItem | null) => void;
  onDoubleClick: (slot: InventorySlot, item: InventoryItem | null) => void;
  onHover: (item: InventoryItem | null) => void;
  onHoverEnd: (item: InventoryItem | null) => void;
  onPress: (item: InventoryItem | null, pointer: Phaser.Input.Pointer) => void;
}

/**
 * 인벤토리 슬롯 내부 클래스
 */
class InventorySlot extends Phaser.GameObjects.Container {
  private bg!: Phaser.GameObjects.Rectangle;
  /** SVG 아이콘. 텍스처를 못 받으면 숨기고 iconText로 폴백한다. */
  private iconImage!: Phaser.GameObjects.Image;
  /** 아이콘 폴백 텍스트('*'). 로드 실패 시에만 보인다. */
  private iconText!: Phaser.GameObjects.Text;
  private countText!: Phaser.GameObjects.Text;
  private enchantText!: Phaser.GameObjects.Text;
  /** 장착 중 표시 뱃지. 슬롯 우상단 'E'. */
  private equippedBadge!: Phaser.GameObjects.Text;
  private item: InventoryItem | null = null;
  private slotIndex: number;
  private isSelected: boolean = false;
  private isHovered: boolean = false;
  private isEquipped: boolean = false;

  /** 아이콘을 그릴 한 변의 길이. setTexture()마다 다시 적용해야 한다. */
  private iconSize: number = 0;

  /**
   * 아이콘 로드가 비동기라, 로드 중에 스크롤로 슬롯 내용이 바뀌면 늦게 도착한
   * 텍스처가 엉뚱한 아이템에 붙는다. setItem()마다 증가시켜 뒤늦은 응답을 버린다.
   */
  private iconToken: number = 0;

  private handlers: InventorySlotHandlers;

  private lastClickTime: number = 0;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    size: number,
    slotIndex: number,
    handlers: InventorySlotHandlers
  ) {
    super(scene, x, y);
    this.slotIndex = slotIndex;
    this.handlers = handlers;

    this.createUI(size);
    scene.add.existing(this);
  }

  private createUI(size: number): void {
    // 슬롯 배경
    this.bg = this.scene.add.rectangle(0, 0, size, size, SLOT_BG_COLOR);
    this.bg.setStrokeStyle(1, SLOT_BORDER_COLOR);
    this.add(this.bg);

    // 아이템 아이콘 (카테고리 SVG). 텍스처가 정해지기 전에는 숨겨 둔다 —
    // 빈 키로 만들면 Phaser가 '__MISSING' 체크무늬를 그린다.
    this.iconSize = size - 10;
    this.iconImage = this.scene.add.image(0, -2, '__DEFAULT');
    this.iconImage.setVisible(false);
    this.add(this.iconImage);

    // 아이콘 폴백 텍스트. SVG를 못 받았을 때만 보인다.
    this.iconText = this.scene.add.text(0, -4, '', {
      fontSize: '20px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff'
    });
    this.iconText.setOrigin(0.5);
    this.add(this.iconText);

    // 개수
    this.countText = this.scene.add.text(size / 2 - 2, size / 2 - 6, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff'
    });
    this.countText.setOrigin(1, 1);
    this.add(this.countText);

    // 인챈트 레벨
    this.enchantText = this.scene.add.text(-size / 2 + 2, -size / 2 + 2, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffd700',
      fontStyle: 'bold'
    });
    this.enchantText.setOrigin(0, 0);
    this.add(this.enchantText);

    // 장착 표시 뱃지. 좌상단은 인챈트, 우하단은 개수가 이미 쓰고 있어 우상단에 둔다.
    // 아이콘 그림 위에 겹치므로 어두운 바탕을 깔아야 글자가 묻히지 않는다.
    this.equippedBadge = this.scene.add.text(size / 2 - 2, -size / 2 + 2, '', {
      fontSize: '10px',
      fontFamily: 'Arial, sans-serif',
      color: EQUIPPED_TEXT_COLOR,
      fontStyle: 'bold',
      backgroundColor: '#12301c',
      padding: { x: 2, y: 1 }
    });
    this.equippedBadge.setOrigin(1, 0);
    // 빈 문자열이어도 바탕색은 그려지므로 표시 여부는 setVisible로 끈다.
    this.equippedBadge.setVisible(false);
    this.add(this.equippedBadge);

    // 클릭 가능 영역
    // 컨테이너의 setScrollFactor(0)은 자식에 상속되지 않아 직접 걸어야 한다
    // (카메라 스크롤 중 클릭 히트테스트가 어긋나는 문제 방지).
    const hitArea = this.scene.add.rectangle(0, 0, size, size, 0x000000, 0);
    hitArea.setInteractive({ useHandCursor: true });
    hitArea.setScrollFactor(0);

    hitArea.on('pointerover', () => {
      this.isHovered = true;
      this.refreshBorder();
      this.handlers.onHover(this.item);
    });

    hitArea.on('pointerout', () => {
      this.isHovered = false;
      this.refreshBorder();
      this.handlers.onHoverEnd(this.item);
    });

    hitArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const now = Date.now();
      const isDoubleClick = now - this.lastClickTime < 300;
      this.lastClickTime = now;

      if (isDoubleClick && this.item) {
        this.handlers.onDoubleClick(this, this.item);
      } else {
        this.handlers.onClick(this, this.item);
      }

      // 클릭 처리와 별개로 드래그 후보를 부모에 알린다. 선택은 이미 끝났으므로
      // 이어서 끌기 시작해도 화면 상태가 어긋나지 않는다.
      this.handlers.onPress(this.item, pointer);
    });

    this.add(hitArea);
  }

  public setItem(item: InventoryItem): void {
    this.item = item;

    // 아이템 아이콘 — gfxId(= 서버 inv_gfx_id)를 카테고리 아이콘으로 접는다.
    this.applyIcon(item);

    // 개수
    if (item.count > 1) {
      this.countText.setText(`x${item.count}`);
    } else {
      this.countText.setText('');
    }

    // 인챈트
    if (item.enchantLevel > 0) {
      this.enchantText.setText(`+${item.enchantLevel}`);
      this.enchantText.setStyle({ color: '#ffd700' });
    } else if (item.enchantLevel < 0) {
      this.enchantText.setText(`${item.enchantLevel}`);
      this.enchantText.setStyle({ color: '#ff4444' });
    } else {
      this.enchantText.setText('');
    }

    // 장착 표시 — 뱃지 + 배경 + 테두리를 한꺼번에 갱신한다.
    this.isEquipped = item.equipped === true;
    this.equippedBadge.setText('E');
    this.equippedBadge.setVisible(this.isEquipped);
    this.bg.setFillStyle(this.isEquipped ? SLOT_BG_EQUIPPED_COLOR : SLOT_BG_COLOR);
    this.refreshBorder();

    // 미감정 아이템은 흐리게
    const alpha = item.identified ? 1 : 0.5;
    this.iconImage.setAlpha(alpha);
    this.iconText.setAlpha(alpha);
  }

  /**
   * 카테고리 SVG 아이콘을 슬롯에 붙인다.
   *
   * 이미 등록된 텍스처면 동기로 바로 붙인다 — 매 렌더마다 Promise를 태우면
   * 스크롤할 때 한 프레임씩 아이콘이 비어 깜빡인다. 로드에 실패하면 예전처럼
   * '*' 텍스트로 폴백해 최소한 칸이 비어 보이지는 않게 한다.
   */
  private applyIcon(item: InventoryItem): void {
    const kind = itemIconKind(item.gfxId, item.name);
    const key = itemIconTextureKey(kind);
    const token = ++this.iconToken;

    if (this.scene.textures.exists(key)) {
      this.showIconTexture(key);
      return;
    }

    // 아직 없으면 받아 온다. 그 사이에는 폴백 문자를 띄워 칸이 비지 않게 한다.
    this.showIconFallback(item);
    loadItemIcon(this.scene, kind)
      .then((loadedKey) => {
        if (token !== this.iconToken) return; // 그 사이 슬롯 내용이 바뀌었다
        this.showIconTexture(loadedKey);
      })
      .catch(() => {
        // 폴백 문자는 이미 떠 있으므로 그대로 둔다.
      });
  }

  private showIconTexture(key: string): void {
    this.iconImage.setTexture(key);
    // setTexture()는 새 프레임 크기로 width/height를 되돌리므로 크기를 다시 잡는다.
    // (한 번만 잡아두면 __DEFAULT 프레임 기준으로 계산된 배율이 남아 아이콘이 커진다.)
    this.iconImage.setDisplaySize(this.iconSize, this.iconSize);
    this.iconImage.setVisible(true);
    this.iconText.setText('');
  }

  private showIconFallback(item: InventoryItem): void {
    this.iconImage.setVisible(false);
    this.iconText.setText('*');
    this.iconText.setStyle({ color: item.identified ? '#ffffff' : '#888888' });
  }

  public clear(): void {
    this.item = null;
    this.iconToken++;
    this.iconImage.setVisible(false);
    this.iconText.setText('');
    this.countText.setText('');
    this.enchantText.setText('');
    this.equippedBadge.setVisible(false);
    this.iconImage.setAlpha(1);
    this.iconText.setAlpha(1);

    this.isEquipped = false;
    this.bg.setFillStyle(SLOT_BG_COLOR);
    this.refreshBorder();
  }

  public setSelected(selected: boolean): void {
    this.isSelected = selected;
    this.refreshBorder();
  }

  /**
   * 테두리 한 곳에서만 그린다.
   * 우선순위: 선택 > 장착 > 마우스 오버 > 기본.
   * (분산시키면 장착 슬롯에 마우스를 올렸다 뗄 때 장착 테두리가 지워진다.)
   */
  private refreshBorder(): void {
    if (this.isSelected) {
      this.bg.setStrokeStyle(2, SLOT_SELECTED_COLOR);
    } else if (this.isEquipped) {
      this.bg.setStrokeStyle(2, EQUIPPED_BORDER_COLOR);
    } else if (this.isHovered) {
      this.bg.setStrokeStyle(1, SLOT_HOVER_COLOR);
    } else {
      this.bg.setStrokeStyle(1, SLOT_BORDER_COLOR);
    }
  }

  public getItem(): InventoryItem | null {
    return this.item;
  }
}
